import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAccessActions } from 'pricing/hooks';
import { IPricingProfileAccessItem } from 'pricing/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  pricingProfileAccessItems: IPricingProfileAccessItem[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
  notificationPosition?: NotificationPosition;
}

export const RemovePricingProfileAccessButton = ({
  pricingProfileAccessItems,
  buttonElement,
  buttonLabel,
  onRemoveSuccess,
  disabled = false,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { removeAccess } = useAccessActions({
    selectionCount: pricingProfileAccessItems.length,
    position: notificationPosition,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const removedAccessIds = pricingProfileAccessItems.map((accessItem) => accessItem.id);

    const { success } = await removeAccess({
      ids: removedAccessIds,
    });

    if (!success) {
      return;
    }

    onRemoveSuccess?.();
  }, [onRemoveSuccess, pricingProfileAccessItems, removeAccess]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, buttonLabel, disabled, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemovePricingProfileAccess', {
          count: pricingProfileAccessItems.length,
        })}
        message={t('messageConfirmRemovePricingProfileAccess', {
          count: pricingProfileAccessItems.length,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
